import { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectCartCount } from '../redux/cartSlice';
import { useCurrency } from '../utils/currency';
import { useGetCategories } from '../backend/hooks';
import logo from '../assets/logo/logo.png';
import { Skeleton } from './skeleton';

export default function Navbar() {
    const [isShopDropdownOpen, setIsShopDropdownOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const { data, isLoading: loading, error } = useGetCategories();
    const cartCount = useSelector(selectCartCount);
    const { isPakistan } = useCurrency();
    const dropdownRef = useRef(null);
    const location = useLocation();

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsShopDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const toggleDropdown = () => setIsShopDropdownOpen(!isShopDropdownOpen);
    const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
    const closeMobileMenu = () => setIsMobileMenuOpen(false);

    const isShopActive = isShopDropdownOpen ||
        location.pathname.startsWith('/ProductList') ||
        location.pathname.startsWith('/ProductDetail') ||
        location.pathname === '/Categories';

    // Social icons component – conditionally render based on `platforms` prop
    const SocialIcons = ({ className = "text-white", iconColor = "text-white", platforms = [] }) => {
        // Define all icons with their hrefs
        const allIcons = {
            facebook: {
                href: "https://www.facebook.com/kinetixpk",
                svg: (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                ),
                label: "Facebook"
            },
            instagram: {
                href: "https://www.instagram.com/kinetixpk/",
                svg: (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                    </svg>
                ),
                label: "Instagram"
            },
            youtube: {
                href: "https://youtube.com",
                svg: (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                ),
                label: "YouTube"
            },
            linkedin: {
                href: "https://linkedin.com",
                svg: (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                ),
                label: "LinkedIn"
            },
            tiktok: {
                href: "https://tiktok.com",
                svg: (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M16.6 5.82s.51.5 0 0A4.278 4.278 0 0 1 15.54 3h-3.09v12.4a2.592 2.592 0 0 1-2.59 2.5 2.592 2.592 0 0 1-2.59-2.5 2.592 2.592 0 0 1 2.59-2.5c.266 0 .52.042.764.12v-3.17a5.764 5.764 0 0 0-3.93 1.49 5.71 5.71 0 0 0-1.77 4.06 5.71 5.71 0 0 0 1.77 4.06 5.764 5.764 0 0 0 4.06 1.77 5.764 5.764 0 0 0 4.06-1.77 5.71 5.71 0 0 0 1.77-4.06V9.12a8.644 8.644 0 0 0 4.06 1.77V7.08c-1.44-.29-2.6-1.09-3.38-2.26z"/>
                    </svg>
                ),
                label: "TikTok"
            }
        };

        // Determine which icons to show
        const platformsToShow = platforms.length > 0 ? platforms : Object.keys(allIcons);
        const iconsToRender = platformsToShow.map(key => allIcons[key]).filter(Boolean);

        return (
            <div className={`flex items-center gap-4 ${className}`}>
                {iconsToRender.map((icon, idx) => (
                    <a
                        key={idx}
                        href={icon.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${iconColor} hover:opacity-80 transition-colors`}
                        aria-label={icon.label}
                    >
                        {icon.svg}
                    </a>
                ))}
            </div>
        );
    };

    return (
        <>
            <nav className="fixed top-0 w-full z-50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md shadow-sm dark:shadow-none">
                {/* Top bar – only Facebook & Instagram */}
                <div className="border-b border-white/20 bg-[#60ce80]">
                    <div className="max-w-7xl mx-auto px-4 md:px-6 h-9 flex items-center justify-between">
                        <span className="text-xs font-medium text-white">
                            Welcome to Kinetix Store
                        </span>
                        <div className="hidden sm:flex items-center gap-4">
                            <SocialIcons iconColor="text-white" platforms={['facebook', 'instagram']} />
                        </div>
                    </div>
                </div>

                {/* Main navbar (unchanged) */}
                <div className="flex justify-between items-center max-w-7xl mx-auto px-4 md:px-6 h-20">
                    <NavLink
                        className="text-2xl font-extrabold text-sky-800 dark:text-sky-300 tracking-tighter font-headline"
                        to="/"
                        onClick={closeMobileMenu}
                    >
                        <img src={logo} width={150} height="auto" alt="Logo" />
                    </NavLink>

                    <div className="hidden md:flex items-center space-x-8">
                        <NavLink
                            to="/"
                            end
                            className={({ isActive }) =>
                                `font-['Manrope'] font-bold tracking-tight hover:text-sky-600 dark:hover:text-sky-200 transition-colors duration-300 pb-1 ${
                                    isActive
                                        ? 'border-b-2 border-sky-800 dark:border-sky-300 text-sky-800 dark:text-sky-300'
                                        : 'border-b-2 border-transparent text-slate-600 dark:text-slate-400'
                                }`
                            }
                        >
                            Home
                        </NavLink>

                        <div className="relative" ref={dropdownRef}>
                            <button
                                onClick={toggleDropdown}
                                className={`flex items-center gap-1 font-['Manrope'] font-bold tracking-tight transition-colors duration-300 bg-transparent pb-1 ${
                                    isShopActive
                                        ? 'text-sky-700 dark:text-sky-200 border-b-2 border-sky-800 dark:border-sky-300'
                                        : 'text-slate-600 dark:text-slate-400 hover:text-sky-700 dark:hover:text-sky-200 border-b-2 border-transparent'
                                }`}
                            >
                                Shop
                                <svg
                                    className={`w-4 h-4 transition-transform duration-200 ${isShopDropdownOpen ? 'rotate-180' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                            {isShopDropdownOpen && (
                                <div className="absolute left-0 mt-2 w-72 bg-white dark:bg-slate-800 rounded-lg shadow-lg py-2 z-50 animate-fadeIn">
                                    {/* Fixed-height, scrollable category list so long lists don't overflow the page */}
                                    <div className="max-h-80 overflow-y-auto">
                                    {loading && (
                                        <div className="px-4 py-2 space-y-4">
                                            {[70, 55, 80, 60, 45].map((w) => (
                                                <Skeleton key={w} className="h-4 rounded" style={{ width: `${w}%` }} />
                                            ))}
                                        </div>
                                    )}
                                    {error && <div className="px-4 py-2 text-sm text-red-500">Failed to load categories</div>}
                                    {data?.data?.map((cat) => (
                                        <NavLink
                                            to={`/ProductList/${cat.id}`}
                                            key={cat.id}
                                            onClick={() => setIsShopDropdownOpen(false)}
                                            className={({ isActive }) =>
                                                `block px-4 py-2 font-['Manrope'] transition-colors duration-200 ${
                                                    isActive
                                                        ? 'bg-sky-100 dark:bg-slate-700 text-sky-800 dark:text-sky-200'
                                                        : 'text-slate-700 dark:text-slate-300 hover:bg-sky-50 dark:hover:bg-slate-700'
                                                }`
                                            }
                                        >
                                            {cat?.name}
                                        </NavLink>
                                    ))}
                                    </div>
                                    <div className="border-t border-slate-200 dark:border-slate-700 mt-1 pt-1">
                                        <NavLink
                                            to="/Categories"
                                            onClick={() => setIsShopDropdownOpen(false)}
                                            className={({ isActive }) =>
                                                `block px-4 py-2 font-['Manrope'] font-semibold transition-colors duration-200 ${
                                                    isActive
                                                        ? 'bg-sky-100 dark:bg-slate-700 text-sky-800 dark:text-sky-200'
                                                        : 'text-sky-700 dark:text-sky-400 hover:bg-sky-50 dark:hover:bg-slate-700'
                                                }`
                                            }
                                        >
                                            See all categories →
                                        </NavLink>
                                    </div>
                                </div>
                            )}
                        </div>

                        <NavLink
                            to="/BulkOrder"
                            className={({ isActive }) =>
                                `font-['Manrope'] font-bold tracking-tight transition-colors duration-300 pb-1 ${
                                    isActive
                                        ? 'border-b-2 border-sky-800 dark:border-sky-300 text-sky-800 dark:text-sky-300'
                                        : 'border-b-2 border-transparent text-slate-600 dark:text-slate-400 hover:text-sky-700 dark:hover:text-sky-200'
                                }`
                            }
                        >
                            Bulk Order
                        </NavLink>
                        <NavLink
                            to="/about"
                            className={({ isActive }) =>
                                `font-['Manrope'] font-bold tracking-tight transition-colors duration-300 pb-1 ${
                                    isActive
                                        ? 'border-b-2 border-sky-800 dark:border-sky-300 text-sky-800 dark:text-sky-300'
                                        : 'border-b-2 border-transparent text-slate-600 dark:text-slate-400 hover:text-sky-700 dark:hover:text-sky-200'
                                }`
                            }
                        >
                            About Us
                        </NavLink>
                        <NavLink
                            to="/contactus"
                            className={({ isActive }) =>
                                `font-['Manrope'] font-bold tracking-tight transition-colors duration-300 pb-1 ${
                                    isActive
                                        ? 'border-b-2 border-sky-800 dark:border-sky-300 text-sky-800 dark:text-sky-300'
                                        : 'border-b-2 border-transparent text-slate-600 dark:text-slate-400 hover:text-sky-700 dark:hover:text-sky-200'
                                }`
                            }
                        >
                            Contact Us
                        </NavLink>
                    </div>

                    <div className="flex items-center space-x-5">
                        {isPakistan && (
                            <NavLink
                                to="/Cart"
                                className={({ isActive }) =>
                                    `relative p-2 rounded-full transition-all ${
                                        isActive
                                            ? 'text-sky-800 dark:text-sky-200 bg-slate-100/50 dark:bg-slate-800/50'
                                            : 'text-sky-800 dark:text-sky-400 hover:bg-slate-100/50 dark:hover:bg-slate-800/50'
                                    }`
                                }
                            >
                                <span className="material-symbols-outlined">shopping_cart</span>
                                {cartCount > 0 && (
                                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                                        {cartCount > 9 ? '9+' : cartCount}
                                    </span>
                                )}
                            </NavLink>
                        )}
                        <button
                            onClick={toggleMobileMenu}
                            className="md:hidden p-2 text-sky-800 dark:text-sky-400 hover:bg-slate-100/50 dark:hover:bg-slate-800/50 rounded-lg transition-colors"
                            aria-label="Toggle menu"
                        >
                            <span className="material-symbols-outlined">
                                {isMobileMenuOpen ? 'close' : 'menu'}
                            </span>
                        </button>
                    </div>
                </div>
            </nav>

            {/* Mobile menu – all social icons included */}
            <div
                className={`fixed top-0 left-0 h-full w-64 bg-white dark:bg-slate-900 z-50 shadow-2xl transition-transform duration-300 ease-in-out ${
                    isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
                } md:hidden`}
            >
                <div className="flex justify-between items-center p-6 border-b border-slate-200 dark:border-slate-700">
                    <span className="text-xl font-bold text-sky-800 dark:text-sky-300">Menu</span>
                    <button onClick={closeMobileMenu} className="p-2">
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>
                <nav className="flex flex-col p-4 space-y-2 flex-1 overflow-y-auto">
                    <NavLink to="/" onClick={closeMobileMenu} className="block px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                        Home
                    </NavLink>
                    <NavLink to="/Categories" onClick={closeMobileMenu} className="block px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                        Shop
                    </NavLink>
                    <NavLink to="/BulkOrder" onClick={closeMobileMenu} className="block px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                        Bulk Order
                    </NavLink>
                    <NavLink to="/about" onClick={closeMobileMenu} className="block px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                        About Us
                    </NavLink>
                    <NavLink to="/contactus" onClick={closeMobileMenu} className="block px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                        Contact Us
                    </NavLink>
                    <div className="border-t border-slate-200 dark:border-slate-700 pt-4 mt-2">
                        {isPakistan && (
                            <NavLink to="/Cart" onClick={closeMobileMenu} className="block px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                                Cart {cartCount > 0 && `(${cartCount})`}
                            </NavLink>
                        )}
                    </div>

                    {/* Social Icons in sidebar – all platforms */}
                    <div className="mt-auto pt-6 border-t border-slate-200 dark:border-slate-700">
                        <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
                            Follow Us
                        </p>
                        <SocialIcons
                            className="justify-center"
                            iconColor="text-slate-600 dark:text-slate-300 hover:text-sky-600 dark:hover:text-sky-400"
                        />
                    </div>
                </nav>
            </div>

            {/* Overlay */}
            {isMobileMenuOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 md:hidden"
                    onClick={closeMobileMenu}
                ></div>
            )}
        </>
    );
}